// src/layouts/crm/templates/index.js
/* eslint-disable react/prop-types */
// Προβολή λίστας Templates + Drawer για Προσθήκη/Επεξεργασία

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MDAvatar from "components/MDAvatar";
import Icon from "@mui/material/Icon";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentPaste as PlaceholderIcon,
} from "@mui/icons-material";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Supabase
import { supabase } from "lib/supa";

// Template helpers
import { MissingKeysMsg } from "components/MissingKeysMsg";
import { extractPlaceholders, applyTemplate, buildTemplateVars } from "../../../lib/buildtemplate";

// Διαθέσιμα placeholders σύμφωνα με buildTemplateVars (helper context)
const AVAILABLE_PLACEHOLDERS = [
  "days",
  "service",
  "deadline",
  "cost",
  "title",
  "start_date",
  "end_date",
  "days_left",
  "client_fullname",
  "account_name",
  "service_id",
];

// ===============
// Utils
// ===============
function getMissingKeysFor(templateStr = "", varsObj = {}) {
  const keysInTemplate = extractPlaceholders(templateStr) || [];
  const known = new Set(Object.keys(varsObj));
  return keysInTemplate.filter((k) => !known.has(k));
}

function insertAtCursor(textarea, insertText) {
  if (!textarea) return;
  const start = textarea.selectionStart ?? textarea.value.length;
  const end = textarea.selectionEnd ?? textarea.value.length;
  const before = textarea.value.substring(0, start);
  const after = textarea.value.substring(end);
  const next = `${before}{${insertText}}${after}`;
  textarea.value = next;
  const pos = start + insertText.length + 2; // include { }
  textarea.selectionStart = textarea.selectionEnd = pos;
  // Trigger input event so React onChange fires
  const ev = new Event("input", { bubbles: true });
  textarea.dispatchEvent(ev);
}

// ===============
// Main Component
// ===============
export default function TemplatesPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [deleteRow, setDeleteRow] = useState(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // false = create, true = edit
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    id: null,
    name: "",
    subject: "",
    body_html: "",
    body_sms: "",
    is_active: true,
  });

  // Tabs per field
  const [tabSubject, setTabSubject] = useState(0); // 0=Κείμενο, 1=Προεπισκόπηση
  const [tabHtml, setTabHtml] = useState(0);
  const [tabSms, setTabSms] = useState(0);

  // Placeholder popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [placeholderTarget, setPlaceholderTarget] = useState(null); // "subject" | "body_html" | "body_sms"

  // Textarea refs για εισαγωγή placeholder στο cursor
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const smsRef = useRef(null);

  // caret state + input refs
  const [caret, setCaret] = useState({
    subject: { start: 0, end: 0 },
    body_html: { start: 0, end: 0 },
    body_sms: { start: 0, end: 0 },
  });
  const subjectInputRef = useRef(null);
  const htmlInputRef = useRef(null);
  const smsInputRef = useRef(null);

  // Preview vars (από πιο πρόσφατη υπηρεσία)
  const [previewVars, setPreviewVars] = useState({});

  const [search, setSearch] = useState(""); // search state

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notification_templates")
        .select("id, name, subject, is_active, updated_at")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      setRows(
        (data || []).map((r) => ({
          id: r.id,
          name: r.name,
          subject: r.subject || "",
          is_active: r.is_active,
          updated_at: r.updated_at,
        }))
      );
    } catch (err) {
      console.error("fetchTemplates error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreviewVars = useCallback(async () => {
    // Παίρνουμε την πιο πρόσφατη υπηρεσία του account για buildTemplateVars
    try {
      const { data: svc, error } = await supabase
        .from("services")
        .select("*")
        .order("end_date", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const vars = buildTemplateVars(svc || null, {
        offsetDays: 0,
        locale: "el-GR",
        currency: "EUR",
      });
      setPreviewVars(vars || {});
    } catch (err) {
      console.warn("fetchPreviewVars", err);
      // Fallback: κενό set
      const fallback = {};
      AVAILABLE_PLACEHOLDERS.forEach((k) => (fallback[k] = "—"));
      setPreviewVars(fallback);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchPreviewVars();
  }, [fetchTemplates, fetchPreviewVars]);

  // Columns for DataTable
  const columns = useMemo(
    () => [
      {
        Header: "Ενεργειες",
        accessor: "actions",
        width: "120px",
        disableSortBy: true,
        Cell: ({ row }) => (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Επεξεργασία">
              <IconButton
                size="small"
                color="warning" // changed: add warning color
                onClick={() => handleEdit(row.original)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Διαγραφή">
              <IconButton size="small" color="error" onClick={() => setDeleteRow(row.original)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
      { Header: "Ονομα", accessor: "name", disableSortBy: true },
      { Header: "Θεμα", accessor: "subject", disableSortBy: true },
      {
        Header: "Ενεργo",
        accessor: "is_active",
        disableSortBy: true,
        Cell: ({ value }) => (
          <Chip label={value ? "Ναι" : "Όχι"} color={value ? "success" : "error"} size="small" />
        ),
      },
    ],
    []
  );

  // Apply search filtering to rows before passing to DataTable
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.name || "").toLowerCase();
      const subject = String(r.subject || "").toLowerCase();
      return name.includes(q) || subject.includes(q);
    });
  }, [rows, search]);

  const data = useMemo(
    () => ({
      columns,
      rows: filteredRows.map((r) => ({ ...r, actions: "" })),
    }),
    [columns, filteredRows]
  );

  // Handlers
  const handleCreate = () => {
    setEditMode(false);
    setForm({ id: null, name: "", subject: "", body_html: "", body_sms: "", is_active: true });
    setTabSubject(0);
    setTabHtml(0);
    setTabSms(0);
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    setEditMode(true);
    // Χρειαζόμαστε και body_html/body_sms -> fetch single
    (async () => {
      try {
        const { data, error } = await supabase
          .from("notification_templates")
          .select("*")
          .eq("id", row.id)
          .single();
        if (error) throw error;
        setForm({
          id: data.id,
          name: data.name || "",
          subject: data.subject || "",
          body_html: data.body_html || "",
          body_sms: data.body_sms || "",
          is_active: !!data.is_active,
        });
        setTabSubject(0);
        setTabHtml(0);
        setTabSms(0);
        setDrawerOpen(true);
      } catch (err) {
        console.error("load template error", err);
      }
    })();
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    try {
      const { error } = await supabase
        .from("notification_templates")
        .delete()
        .eq("id", deleteRow.id);
      if (error) throw error;
      setDeleteRow(null);
      await fetchTemplates(); // αυστηρό refetch
    } catch (err) {
      console.error("delete error", err);
    }
  };

  const handleSave = async () => {
    // Validation: τουλάχιστον subject ή body_sms
    const hasSubject = (form.subject || "").trim().length > 0;
    const hasSms = (form.body_sms || "").trim().length > 0;
    if (!hasSubject && !hasSms) {
      alert("Συμπλήρωσε τουλάχιστον Θέμα ή SMS κείμενο.");
      return;
    }

    setSaving(true);
    try {
      if (editMode && form.id) {
        const payload = {
          name: form.name,
          subject: form.subject || null,
          body_html: form.body_html || null,
          body_sms: form.body_sms || null,
          is_active: !!form.is_active,
        };
        const { error } = await supabase
          .from("notification_templates")
          .update(payload)
          .eq("id", form.id);
        if (error) throw error;
      } else {
        const payload = {
          name: form.name,
          subject: form.subject || null,
          body_html: form.body_html || null,
          body_sms: form.body_sms || null,
          is_active: !!form.is_active,
        };
        const { error } = await supabase.from("notification_templates").insert(payload);
        if (error) throw error;
      }
      setDrawerOpen(false);
      await fetchTemplates(); // αυστηρό refetch
    } catch (err) {
      console.error("save error", err);
      alert("Αποτυχία αποθήκευσης. Δοκίμασε ξανά.");
    } finally {
      setSaving(false);
    }
  };

  // Placeholder picker
  const openPlaceholderPicker = (ev, targetField) => {
    setAnchorEl(ev.currentTarget);
    setPlaceholderTarget(targetField);
  };
  const closePlaceholderPicker = () => {
    setAnchorEl(null);
    setPlaceholderTarget(null);
  };

  // helper: update caret positions
  const makeCaretHandler = (field) => (e) => {
    const t = e.target;
    if (!t) return;
    const start = t.selectionStart ?? 0;
    const end = t.selectionEnd ?? start;
    setCaret((c) => ({ ...c, [field]: { start, end } }));
  };

  // Εισαγωγή placeholder στο state αντί για άμεσο DOM mutation
  const onPickPlaceholder = (key) => {
    const field = placeholderTarget;
    if (!field) {
      closePlaceholderPicker();
      return;
    }
    const cur = form[field] || "";
    const pos = caret[field] || { start: cur.length, end: cur.length };
    const insert = `{${key}}`;
    const next = `${cur.slice(0, pos.start)}${insert}${cur.slice(pos.end)}`;
    setForm((s) => ({ ...s, [field]: next }));
    closePlaceholderPicker();

    // επανάφερε focus και caret μετά την εισαγωγή
    setTimeout(() => {
      const mapRef = {
        subject: subjectInputRef,
        body_html: htmlInputRef,
        body_sms: smsInputRef,
      };
      const el = mapRef[field]?.current;
      if (el?.focus && el.setSelectionRange) {
        const newPos = pos.start + insert.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
        setCaret((c) => ({ ...c, [field]: { start: newPos, end: newPos } }));
      }
    }, 0);
  };

  // Previews (applyTemplate με previewVars)
  const previewSubject = useMemo(
    () => applyTemplate(form.subject || "", previewVars),
    [form.subject, previewVars]
  );
  const previewHtml = useMemo(
    () => applyTemplate(form.body_html || "", previewVars),
    [form.body_html, previewVars]
  );
  const previewSms = useMemo(
    () => applyTemplate(form.body_sms || "", previewVars),
    [form.body_sms, previewVars]
  );

  // Missing keys per field
  const missingSubject = useMemo(
    () => getMissingKeysFor(form.subject, previewVars),
    [form.subject, previewVars]
  );
  const missingHtml = useMemo(
    () => getMissingKeysFor(form.body_html, previewVars),
    [form.body_html, previewVars]
  );
  const missingSms = useMemo(
    () => getMissingKeysFor(form.body_sms, previewVars),
    [form.body_sms, previewVars]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    ΠΡΟΤΥΠΑ
                  </MDTypography>
                  <Tooltip title="ΝΕΟ ΠΡΟΤΥΠΟ" arrow>
                    <MDAvatar
                      size="sm"
                      bgColor="dark"
                      sx={{ cursor: "pointer" }}
                      onClick={handleCreate}
                    >
                      <Icon sx={{ color: "white" }}>add</Icon>
                    </MDAvatar>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Search bar (left aligned) */}
              <MDBox px={2} pt={2} pb={1} display="flex" justifyContent="flex-start">
                <TextField
                  placeholder="Αναζήτηση προτύπου..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  sx={{ minWidth: 260 }}
                />
              </MDBox>

              {/* Body */}
              {loading ? (
                <MDBox p={3} display="flex" justifyContent="center">
                  <CircularProgress />
                </MDBox>
              ) : (
                <DataTable
                  table={data}
                  isSorted={false}
                  entriesPerPage={false} // no paging
                  showTotalEntries={false}
                  canSearch={false} // disable built-in search
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        PaperProps={{
          sx: { bgcolor: "background.default", color: "text.primary", borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ bgcolor: "dark.main", color: "dark.contrastText" }}>
          Διαγραφή προτύπου;
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", color: "text.primary" }}>
          <MDTypography variant="button">
            Θα διαγραφεί οριστικά το «{deleteRow?.name}». Συνέχεια;
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <Button onClick={() => setDeleteRow(null)}>Άκυρο</Button>
          <Button color="error" onClick={handleDelete}>
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer Create/Edit */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 720, p: 2 } }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <MDTypography variant="h6">
            {editMode ? "Επεξεργασία Προτύπου" : "Νέο Πρότυπο"}
          </MDTypography>
          <Box />
        </MDBox>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Όνομα"
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
                />
              }
              label="Ενεργό"
            />
          </Grid>

          {/* SUBJECT */}
          <Grid item xs={12}>
            <Card>
              <Tabs
                value={tabSubject}
                onChange={(_, v) => setTabSubject(v)}
                aria-label="subject tabs"
              >
                <Tab label="Θέμα (Email)" />
                <Tab label="Προεπισκόπηση" />
              </Tabs>
              <Box ref={subjectRef} hidden={tabSubject !== 0}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <Tooltip title="Εισαγωγή placeholder">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => openPlaceholderPicker(e, "subject")}
                    >
                      <PlaceholderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
                <TextField
                  value={form.subject}
                  onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  inputRef={subjectInputRef}
                  onSelect={makeCaretHandler("subject")}
                  onKeyUp={makeCaretHandler("subject")}
                  onClick={makeCaretHandler("subject")}
                />
              </Box>
              <Box p={2} hidden={tabSubject !== 1}>
                <MDBox mt={1}>
                  <TextField
                    fullWidth
                    multiline
                    size="small"
                    minRows={2}
                    value={previewSubject || ""}
                    InputProps={{ readOnly: true }}
                  />
                </MDBox>
                <MDBox mt={1}>{MissingKeysMsg(missingSubject)}</MDBox>
              </Box>
            </Card>
          </Grid>

          {/* BODY HTML */}
          <Grid item xs={12}>
            <Card>
              <Tabs value={tabHtml} onChange={(_, v) => setTabHtml(v)} aria-label="html tabs">
                <Tab label="HTML (Email σώμα)" />
                <Tab label="Προεπισκόπηση" />
              </Tabs>
              <Box ref={htmlRef} hidden={tabHtml !== 0}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <Tooltip title="Εισαγωγή placeholder">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => openPlaceholderPicker(e, "body_html")}
                    >
                      <PlaceholderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
                <TextField
                  value={form.body_html}
                  onChange={(e) => setForm((s) => ({ ...s, body_html: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={8}
                  placeholder="Γράψε HTML. Μπορείς να χρησιμοποιήσεις {placeholders}."
                  inputRef={htmlInputRef}
                  onSelect={makeCaretHandler("body_html")}
                  onKeyUp={makeCaretHandler("body_html")}
                  onClick={makeCaretHandler("body_html")}
                />
              </Box>
              <Box p={2} hidden={tabHtml !== 1}>
                <MDBox mt={1}>
                  <TextField
                    fullWidth
                    multiline
                    size="small"
                    minRows={8}
                    value={previewHtml || ""}
                    InputProps={{ readOnly: true }}
                  />
                </MDBox>
                <MDBox mt={1}>{MissingKeysMsg(missingHtml)}</MDBox>
              </Box>
            </Card>
          </Grid>

          {/* BODY SMS */}
          <Grid item xs={12}>
            <Card>
              <Tabs value={tabSms} onChange={(_, v) => setTabSms(v)} aria-label="sms tabs">
                <Tab label="SMS" />
                <Tab label="Προεπισκόπηση" />
              </Tabs>
              <Box p={2} ref={smsRef} hidden={tabSms !== 0}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <Tooltip title="Εισαγωγή placeholder">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => openPlaceholderPicker(e, "body_sms")}
                    >
                      <PlaceholderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
                <TextField
                  value={form.body_sms}
                  onChange={(e) => setForm((s) => ({ ...s, body_sms: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Κείμενο SMS. Μπορείς να χρησιμοποιήσεις {placeholders}."
                  inputRef={smsInputRef}
                  onSelect={makeCaretHandler("body_sms")}
                  onKeyUp={makeCaretHandler("body_sms")}
                  onClick={makeCaretHandler("body_sms")}
                />
              </Box>
              <Box p={2} hidden={tabSms !== 1}>
                <MDBox mt={1}>
                  <TextField
                    fullWidth
                    multiline
                    size="small"
                    minRows={4}
                    value={previewSms || ""}
                    InputProps={{ readOnly: true }}
                  />
                </MDBox>
                <MDBox mt={1}>{MissingKeysMsg(missingSms)}</MDBox>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <MDBox display="flex" gap={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setDrawerOpen(false)}>
                Άκυρο
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: "success.main",
                  color: "success.contrastText",
                  "&:hover": { bgcolor: "success.dark" },
                }}
              >
                {saving ? "Αποθήκευση…" : "Αποθήκευση"}
              </Button>
            </MDBox>
          </Grid>
        </Grid>
      </Drawer>

      {/* Placeholder picker popover */}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePlaceholderPicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            bgcolor: "dark.main",
            color: "dark.contrastText",
            border: "1px solid",
            borderColor: "dark.contrastText",
            boxShadow: 8,
          },
        }}
      >
        <MDBox p={1} maxWidth={300}>
          <MDTypography variant="button" color="white">
            Διαθέσιμα Placeholders
          </MDTypography>
          <MDBox mt={1} display="flex" gap={0.5} flexWrap="wrap">
            {AVAILABLE_PLACEHOLDERS.map((k) => (
              <Chip
                key={k}
                label={`{${k}}`}
                size="small"
                variant="filled"
                color="info"
                onClick={() => onPickPlaceholder(k)}
              />
            ))}
          </MDBox>
        </MDBox>
      </Popover>
    </DashboardLayout>
  );
}
